/**
 * Searchable multi-select popover with checkboxes (B2).
 *
 * The shared UI kit has no multi-select, so this is built on the Radix
 * Popover primitive. Used by the lead form to link listings to a lead;
 * listing counts can exceed what inline chips would show, hence the
 * search + scroll list.
 *
 * Selection is a capped set of option values. When the cap is reached,
 * unselected rows are disabled (already-selected rows stay toggleable so
 * the realtor can still deselect) and a hint is shown.
 */

import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

export interface MultiSelectOption {
	value: string;
	label: string;
	sublabel?: string;
}

export interface SearchableMultiSelectProps {
	options: MultiSelectOption[];
	selected: string[];
	onChange: (next: string[]) => void;
	/** Maximum number of selectable options. */
	max?: number;
	triggerPlaceholder: string;
	/** Called with the count to render the trigger label when >0 selected. */
	triggerLabel: (count: number) => string;
	searchPlaceholder: string;
	noResultsText: string;
	noOptionsText: string;
	capReachedText?: string;
	disabled?: boolean;
	/** id applied to the trigger button so an external `<label htmlFor>` targets it. */
	id?: string;
	"aria-label"?: string;
}

export function SearchableMultiSelect({
	options,
	selected,
	onChange,
	max,
	triggerPlaceholder,
	triggerLabel,
	searchPlaceholder,
	noResultsText,
	noOptionsText,
	capReachedText,
	disabled,
	id,
	"aria-label": ariaLabel,
}: SearchableMultiSelectProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const selectedSet = useMemo(() => new Set(selected), [selected]);
	const capReached = max != null && selected.length >= max;

	const filtered = useMemo(() => {
		const term = query.trim().toLowerCase();
		if (!term) return options;
		return options.filter(
			(o) =>
				o.label.toLowerCase().includes(term) ||
				(o.sublabel?.toLowerCase().includes(term) ?? false),
		);
	}, [options, query]);

	const toggle = (value: string) => {
		if (selectedSet.has(value)) {
			onChange(selected.filter((v) => v !== value));
		} else {
			if (capReached) return;
			onChange([...selected, value]);
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					id={id}
					variant="outline"
					role="combobox"
					aria-expanded={open}
					aria-label={ariaLabel}
					disabled={disabled || options.length === 0}
					className="w-full justify-between font-normal border-dashboard-border bg-dashboard-bg hover:bg-dashboard-surface"
					style={{
						color:
							selected.length === 0
								? "var(--color-dashboard-muted)"
								: "var(--color-dashboard-text)",
					}}
				>
					<span>
						{options.length === 0
							? noOptionsText
							: selected.length === 0
								? triggerPlaceholder
								: triggerLabel(selected.length)}
					</span>
					<ChevronsUpDown
						className="h-4 w-4 opacity-50 shrink-0"
						style={{ color: "var(--color-dashboard-muted)" }}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="border-dashboard-border shadow-[var(--dashboard-shadow-2)]"
				style={{ backgroundColor: "var(--color-dashboard-bg)" }}
				align="start"
			>
				<div
					className="flex items-center gap-2 border-b px-3 py-2"
					style={{ borderColor: "var(--color-dashboard-border)" }}
				>
					<Search
						className="h-4 w-4 shrink-0"
						style={{ color: "var(--color-dashboard-muted)" }}
					/>
					<Input
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={searchPlaceholder}
						className="h-10 border-0 px-0 shadow-none focus-visible:ring-0 bg-transparent"
						style={{
							backgroundColor: "transparent",
							color: "var(--color-dashboard-text)",
						}}
					/>
				</div>
				{capReached && capReachedText && (
					<p
						className="px-3 py-1.5 text-[11px]"
						style={{ color: "var(--color-dashboard-pill-awaiting)" }}
					>
						{capReachedText}
					</p>
				)}
				<div
					className="max-h-56 overflow-y-auto p-1"
					role="listbox"
					aria-multiselectable="true"
				>
					{filtered.length === 0 ? (
						<p
							className="px-3 py-4 text-center text-xs"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{noResultsText}
						</p>
					) : (
						filtered.map((option) => {
							const isSelected = selectedSet.has(option.value);
							const isDisabled = !isSelected && capReached;
							return (
								<button
									key={option.value}
									type="button"
									role="option"
									aria-selected={isSelected}
									disabled={isDisabled}
									onClick={() => toggle(option.value)}
									className="flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-dashboard-surface disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									<span
										className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border"
										style={{
											borderColor: isSelected
												? "var(--color-dashboard-accent)"
												: "var(--color-dashboard-border)",
											backgroundColor: isSelected
												? "var(--color-dashboard-accent)"
												: "transparent",
										}}
										aria-hidden="true"
									>
										{isSelected && (
											<Check className="h-3 w-3" style={{ color: "white" }} />
										)}
									</span>
									<span className="min-w-0">
										<span className="block truncate">{option.label}</span>
										{option.sublabel && (
											<span
												className="block truncate text-xs"
												style={{ color: "var(--color-dashboard-muted)" }}
											>
												{option.sublabel}
											</span>
										)}
									</span>
								</button>
							);
						})
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
